/**
 * Billing — codes, orders and a user's plans, for platform staff.
 *
 * Backend: Backend/functions/src/billing/adminBillingEndpoints.ts.
 * Design: docs/FeatureDesigns/billing/subscriptions-and-payments-target.md §6.3, §11.
 * Keep the shapes in step with Backend/functions/src/billing/types.ts and
 * billingEndpoints.ts (`BillingSummary`).
 */

import type { PlanTier, ResetPeriod } from '@padcanvas/shared';
import { fetchWithAdmin } from './fetchHelper';

export type PurchasablePlan = 'plus' | 'pro';
export const PURCHASABLE_PLANS: readonly PurchasablePlan[] = ['plus', 'pro'];

export type OrderBuyerKind = 'student' | 'parent-link' | 'teacher' | 'school' | 'migration';
export type OrderStatus = 'created' | 'paid' | 'fulfilled' | 'failed' | 'expired';
export const ORDER_STATUSES: readonly OrderStatus[] = ['created', 'paid', 'fulfilled', 'failed', 'expired'];

export interface OrderLine {
  studentId: string;
  /** [] = all subjects. */
  subjects: string[];
  startDate: string;
  endDate: string;
  listPaise: number;
  pricePaise: number;
}

export interface Order {
  id: string;
  /** The buyer. */
  userId: string;
  buyerKind: OrderBuyerKind;
  tenantId: string;
  plan: PurchasablePlan;
  length: 'month' | 'rest-of-month';
  lines: OrderLine[];
  listTotalPaise: number;
  code?: string;
  discountPct?: number;
  totalPaise: number;
  gstPaise: number;
  status: OrderStatus;
  createdAt: string;
  fulfilledAt?: string;
  createdBy: string;
  updatedBy: string;
}

export interface BillingCodeFields {
  discountPct: number;
  plans: PurchasablePlan[];
  /** 'YYYY-MM-DD', from 00:00 IST. */
  validFrom: string;
  /** 'YYYY-MM-DD', to 23:59:59 IST. */
  validUntil: string;
  active: boolean;
}

export interface BillingCode extends BillingCodeFields {
  /** The code, upper case. */
  id: string;
  createdAt: string;
  createdBy: string;
  updatedBy: string;
  /** Fulfilled orders that carried it (list endpoint only). */
  uses?: number;
}

export interface BillingGrant {
  id: string;
  subscriptionId: string;
  name: string;
  tier?: PlanTier;
  startDate: string;
  endDate: string;
  source?: string;
  subjects: string[];
  current: boolean;
}

export interface BillingUsageRow {
  eligibility: string;
  label: string;
  /** A subject, or 'all' for a pooled limit. */
  bucket: string;
  limit: number | null;
  used: number;
  remaining: number | null;
  resetPeriod: ResetPeriod;
  /** For a 'plan' row, the end of the plan's paid month. */
  resetsAt: string | null;
  /** The grant a 'plan' row counts against. */
  grantId?: string;
}

export interface BillingSummary {
  /** `bypassSubscription`: never metered. */
  fullAccess: boolean;
  paymentsOpen: boolean;
  currentTier: PlanTier | null;
  grants: BillingGrant[];
  usage: BillingUsageRow[];
  /** The Plus trial's study days, while one is running. */
  trial: { used: number; total: number } | null;
  /** When the account moved onto the plans; null = never. */
  movedAt: string | null;
  /** The code to renew with while payments are closed. */
  renewalCode: string | null;
}

export interface MoveResult {
  userId: string;
  basicGranted: boolean;
  plan: PurchasablePlan | null;
  orderId?: string;
  cancelledLegacy: number;
  bypassWas: boolean;
}

export interface OrderFilters {
  status?: string;
  code?: string;
  userId?: string;
}

const json = (method: string, body?: unknown): RequestInit => ({
  method,
  ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
});

export const billingApi = {
  listCodes: () => fetchWithAdmin<BillingCode[]>('/console/billing/codes'),

  createCode: (code: BillingCodeFields & { id: string }) =>
    fetchWithAdmin<BillingCode>('/console/billing/codes', json('POST', code)),

  updateCode: (id: string, fields: BillingCodeFields) =>
    fetchWithAdmin<BillingCode>(`/console/billing/codes/${encodeURIComponent(id)}`, json('PUT', fields)),

  codeUses: (id: string) =>
    fetchWithAdmin<Order[]>(`/console/billing/codes/${encodeURIComponent(id)}/uses`),

  listOrders: (filters: OrderFilters) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(filters)) if (v?.trim()) params.set(k, v.trim());
    const qs = params.toString();
    return fetchWithAdmin<Order[]>(`/console/billing/orders${qs ? `?${qs}` : ''}`);
  },

  getOrder: (id: string) =>
    fetchWithAdmin<Order>(`/console/billing/orders/${encodeURIComponent(id)}`),

  fulfilOrder: (id: string) =>
    fetchWithAdmin<Order>(`/console/billing/orders/${encodeURIComponent(id)}/fulfil`, json('POST')),

  getUserBilling: (userId: string) =>
    fetchWithAdmin<BillingSummary>(`/console/users/${encodeURIComponent(userId)}/billing`),

  moveUserToPlans: (userId: string, plan: PurchasablePlan | null) =>
    fetchWithAdmin<MoveResult>(`/console/users/${encodeURIComponent(userId)}/billing/move-to-plans`, json('POST', { plan })),
};

// ── Formatting ───────────────────────────────────────────────────────────

/** ₹ from paise: whole rupees without decimals, otherwise two places. */
export function formatRupees(paise: number | null | undefined): string {
  if (paise == null) return '—';
  const rupees = paise / 100;
  return `₹${rupees.toLocaleString('en-IN', {
    minimumFractionDigits: paise % 100 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
}

export const PLAN_LABEL: Record<string, string> = { basic: 'Basic', plus: 'Plus', pro: 'Pro' };

/** Today's date in India, 'YYYY-MM-DD' — the calendar codes are valid in. */
export function todayIst(now: Date = new Date()): string {
  return new Date(now.getTime() + 5.5 * 60 * 60 * 1000).toISOString().slice(0, 10);
}

export type CodeStatus = 'upcoming' | 'active' | 'expired' | 'off';

export function codeStatus(code: BillingCodeFields, today: string = todayIst()): CodeStatus {
  if (!code.active) return 'off';
  if (today < code.validFrom) return 'upcoming';
  if (today > code.validUntil) return 'expired';
  return 'active';
}

export const CODE_STATUS_LABEL: Record<CodeStatus, string> = {
  upcoming: 'Upcoming', active: 'Active', expired: 'Expired', off: 'Switched off',
};
